// A FELTÖLTÖTT FÁJL → nyers import-sorok. Tiszta modul: nincs React, nincs hálózat.
//
// A böngésző CSAK olvas és sorokká alakít; a FELOLDÁS és minden szabály a szerveré (kategória-út → id,
// munkatárs-név → identity, ütközések). Ez nem kényelmi felosztás: ezek a lépések PÉNZ-HOVATARTOZÁST
// döntenek el, és a repo szabálya szerint üzleti döntés nem élhet a frontenden.
//
// A fájlt SEHOVA nem töltjük fel: nincs új PII tárolva, és nincs feltöltés-infrastruktúra sem.

/** A sablon oszlopai — a FEJLÉC-NÉV azonosít, tehát a sorrend mindegy és az extra oszlop nem baj. */
export const IMPORT_COLUMNS = [
  'issuerKey',
  'supplierName',
  'supplierTaxNumber',
  // A JELÖLÉS a számlaszám ELŐTT áll: előbb dől el, hogy VAN-e bizonylat, és csak utána a száma.
  'noBill',
  'invoiceNumber',
  'issueDate',
  'fulfillmentDate',
  'dueDate',
  'netAmount',
  'vatAmount',
  'currency',
  'categoryPath',
  'employeeName',
  'accountingMonth',
  'note',
] as const;
export type ImportColumn = (typeof IMPORT_COLUMNS)[number];

export interface ParsedSheet {
  /** A sorok a FEJLÉC-NÉV szerinti kulcsokkal; a hiányzó oszlop üres stringet ad. */
  rows: Record<ImportColumn, string>[];
  /** A fájlban talált, de általunk nem ismert fejlécek — a felület kiírja (gyakori elgépelés-jel). */
  unknownHeaders: string[];
  /** A SABLONBÓL hiányzó oszlopok (mind kell, akkor is, ha üresen marad). */
  missingHeaders: ImportColumn[];
}

export class ImportFileError extends Error {}

/** A fejléc-cella → oszlop-kulcs. A magyar FELIRAT és a technikai kulcs is elfogadott. */
function headerKey(label: string, headerLabels: Record<ImportColumn, string>): ImportColumn | null {
  const norm = label.trim().toLowerCase();
  if (norm === '') return null;
  for (const col of IMPORT_COLUMNS) {
    if (norm === col.toLowerCase()) return col;
    if (norm === headerLabels[col].trim().toLowerCase()) return col;
  }
  return null;
}

/** Cella → szöveg. A dátum-cellát NAP-pontos alakra hozzuk: a `Date` `toISOString`-je UTC-ben tolhat. */
function cellToText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'X' : '';
  // A `String(objektum)` „[object Object]"-et adna — ami ÉRTÉKNEK látszana egy cellában. Az ismeretlen
  // alakot inkább ÜRESNEK vesszük: a hiányt a szerver kimondja, egy hamis érték viszont átcsúszna.
  return typeof value === 'string' ? value : '';
}

/**
 * CSV-OLVASÓ (RFC 4180): idézőjeles mező, mezőn belüli sortörés és a `""` escape. A repo CSV-ÍRÓJÁNAK
 * párja — ugyanazt a szabályt kell ismernie, különben a saját exportunkat sem tudnánk visszaolvasni.
 *
 * A BOM-ot eldobjuk: az Excel UTF-8 CSV-je azzal kezdődik, és különben az első fejléc nem illeszkedne.
 */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^\ufeff/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += ch;
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === ',' || ch === ';') {
      row.push(field);
      field = '';
    } else if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (ch !== '\r') field += ch;
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/** Fejléc + adatsorok → oszlop-kulcsos sorok. A teljesen ÜRES sor kimarad (a táblázat alja tele van velük). */
export function sheetToRows(
  table: readonly (readonly string[])[],
  headerLabels: Record<ImportColumn, string>,
): ParsedSheet {
  const header = table[0];
  if (!header) throw new ImportFileError('empty');
  const unknownHeaders: string[] = [];
  const index = new Map<ImportColumn, number>();
  header.forEach((label, i) => {
    const text = label.trim();
    if (text === '') return;
    const key = headerKey(text, headerLabels);
    if (key === null) unknownHeaders.push(text);
    else index.set(key, i);
  });
  const missingHeaders = IMPORT_COLUMNS.filter((c) => !index.has(c));

  const rows = table
    .slice(1)
    .filter((r) => r.some((c) => c.trim() !== ''))
    .map((r) => {
      const out = {} as Record<ImportColumn, string>;
      for (const col of IMPORT_COLUMNS) {
        const at = index.get(col);
        out[col] = at === undefined ? '' : (r[at] ?? '').trim();
      }
      return out;
    });
  return { rows, unknownHeaders, missingHeaders };
}

/**
 * Az XLSX-olvasó KÉTFÉLE alakot ad, és ezen a különbségen a visszatöltés egyszer már elhasalt (mérve, a
 * SAJÁT sablonunkkal): EGY munkalapos fájlra sor-mátrixot, TÖBB munkalaposra `{ sheet, data }` objektumokat.
 * A sablonunk két lapos (a második a választható értékeké), tehát MINDIG a második alak jön — a mátrixként
 * kezelés `r.map is not a function`-t dobott, és a felület „A fájl nem olvasható"-t írt ki.
 *
 * Ezért nem az opciókra hagyatkozunk, hanem az ALAKOT normalizáljuk: az ELSŐ munkalap sorai kellenek,
 * bárhogy is jön. Így egy több lapos, kézzel összerakott munkafüzet is beolvasható.
 */
export function firstSheet(parsed: unknown): unknown[][] {
  if (!Array.isArray(parsed)) return [];
  const [head] = parsed as unknown[];
  if (Array.isArray(head)) return parsed as unknown[][];
  const data = (head as { data?: unknown })?.data;
  return Array.isArray(data) ? (data as unknown[][]) : [];
}

/** A feltöltött fájl beolvasása. `.xlsx` → `read-excel-file`, minden más → CSV-ként. */
export async function readImportFile(
  file: File,
  headerLabels: Record<ImportColumn, string>,
): Promise<ParsedSheet> {
  if (/\.xlsx$/i.test(file.name)) {
    // DINAMIKUS import: a zip+XML olvasó ~200 kB, és a lap 99%-ában nem kell — a fő köteg nem hízik tőle.
    // A `browser` alpont a böngésző-build (a csomag gyökerén nincs típus-belépő, csak alpontonként).
    const { default: readXlsx } = await import('read-excel-file/browser');
    const table = firstSheet(await readXlsx(file));
    return sheetToRows(
      table.map((r) => r.map(cellToText)),
      headerLabels,
    );
  }
  return sheetToRows(parseCsv(await file.text()), headerLabels);
}
